import { useCallback, useRef } from 'react';

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'file',
  'hidden',
  'image',
  'month',
  'radio',
  'range',
  'reset',
  'submit',
  'time',
  'week',
]);

const isIOS = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Comboboxes (e.g. MUI Autocomplete) open on input click, which a cancelled touchend would suppress
const isPlainTextField = (
  el: EventTarget | null
): el is HTMLInputElement | HTMLTextAreaElement => {
  const isField =
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(el.type));
  return (
    isField &&
    !el.disabled &&
    !el.readOnly &&
    el.getAttribute('role') !== 'combobox' &&
    !el.hasAttribute('aria-autocomplete')
  );
};

// Prefers an already-overflowing container; else the nearest scrollable one, which the
// keyboard padding makes overflow (e.g. content that fits on iPad)
const getScrollParent = (el: HTMLElement): HTMLElement | null => {
  let fallback: HTMLElement | null = null;
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      if (node.scrollHeight > node.clientHeight) return node;
      fallback ??= node;
    }
    node = node.parentElement;
  }
  return fallback;
};

const isVisible = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  if (rect.top < 0 || rect.bottom > viewportHeight) return false;
  const hit = document.elementFromPoint(
    rect.left + rect.width / 2,
    rect.top + rect.height / 2
  );
  return !!hit && el.contains(hit);
};

// Scrolls the field's own container (never the window) until it's above the keyboard
const revealInContainer = (el: HTMLElement) => {
  if (isVisible(el)) return;
  const scroller = getScrollParent(el);
  if (!scroller) return;
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  scroller.scrollTop +=
    rect.bottom > viewportHeight
      ? rect.bottom + 16 - viewportHeight
      : rect.top + rect.height / 2 - viewportHeight / 2;
};

// iOS re-centers focused inputs by panning the window, shifting fixed overlays: suppress
// that reveal and scroll the overlay's own container instead, never the window.
export function useOverlayFocusScroll<T extends HTMLElement>() {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((root: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!root || typeof window === 'undefined') return;

    const initialY = window.scrollY;
    let padded: {
      el: HTMLElement;
      paddingBottom: string;
      overscrollBehavior: string;
    } | null = null;
    let touchStart: { x: number; y: number } | null = null;
    let focusingFromTap = false;
    const intercept = isIOS();

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStart =
        e.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
    };

    const onTouchEnd = (e: TouchEvent) => {
      const start = touchStart;
      touchStart = null;
      const el = e.target;
      if (
        !start ||
        !e.cancelable ||
        !isPlainTextField(el) ||
        el === document.activeElement ||
        !getScrollParent(el)
      ) {
        return;
      }
      const touch = e.changedTouches[0];
      if (
        Math.abs(touch.clientX - start.x) > 10 ||
        Math.abs(touch.clientY - start.y) > 10
      ) {
        return;
      }
      e.preventDefault();
      focusingFromTap = true;
      el.focus({ preventScroll: true });
      focusingFromTap = false;
      revealInContainer(el);
    };

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target;
      if (
        !intercept ||
        focusingFromTap ||
        !isPlainTextField(el) ||
        !getScrollParent(el)
      ) {
        return;
      }
      // Keyboard arrows can't pass preventScroll; WebKit skips the reveal while it's transparent
      el.style.opacity = '0';
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          el.style.opacity = '';
          revealInContainer(el);
        })
      );
    };

    const unpad = () => {
      if (!padded) return;
      padded.el.style.paddingBottom = padded.paddingBottom;
      padded.el.style.overscrollBehavior = padded.overscrollBehavior;
      padded = null;
    };

    // While the keyboard is open, pad the container by its height so bottom fields can be
    // scrolled above it, and stop scrolls chaining into the window (which moves the overlay)
    const onViewportResize = () => {
      const el = document.activeElement as HTMLElement | null;
      const viewport = window.visualViewport;
      const keyboardHeight = viewport
        ? window.innerHeight - viewport.height
        : 0;
      if (!el || !root.contains(el) || keyboardHeight < 100) {
        unpad();
        return;
      }
      const scroller = getScrollParent(el);
      if (scroller && padded?.el !== scroller) {
        unpad();
        padded = {
          el: scroller,
          paddingBottom: scroller.style.paddingBottom,
          overscrollBehavior: scroller.style.overscrollBehavior,
        };
        scroller.style.overscrollBehavior = 'contain';
      }
      if (padded) padded.el.style.paddingBottom = `${keyboardHeight}px`;
      revealInContainer(el);
    };

    // Viewport resize only fires once the keyboard has finished closing; unpad as it starts
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget;
      if (
        !(next instanceof Node) ||
        !root.contains(next) ||
        !isPlainTextField(next)
      ) {
        unpad();
      }
    };

    if (intercept) {
      root.addEventListener('touchstart', onTouchStart, { passive: true });
      root.addEventListener('touchend', onTouchEnd, { passive: false });
      root.addEventListener('focusin', onFocusIn);
      root.addEventListener('focusout', onFocusOut);
      window.visualViewport?.addEventListener('resize', onViewportResize);
    }

    cleanupRef.current = () => {
      if (!intercept) return;
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      window.visualViewport?.removeEventListener('resize', onViewportResize);
      unpad();
      if (window.scrollY !== initialY) window.scrollTo(0, initialY);
    };
  }, []);
}
